// @ts-nocheck
import { FILTER_STATUT_APPEL, FILTER_STATUT_LEAD, FILTER_TRI, optionsTraduites, valeursFiltre, filtreActif } from '@/services/dialer/prospectFilters';
import React, { useState, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { Search, SlidersHorizontal, ArrowDown, X, Columns3 } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { useTranslation } from 'react-i18next';
import { dialIntent } from '@/services/dialer/dialRules';

/**
 * #5 — menu de filtre : search bar, cases a cocher, « Tout cocher ».
 *
 * `modes` liste les valeurs EXCLUSIVES (des vues, pas des valeurs cumulables) :
 * les cocher remplace la selection au lieu de s'y ajouter.
 */
function VFFilterDropdown({ label, value, options, onChange, modes = [] }) {
  const { t } = useTranslation(['dialer', 'common']);
  const [recherche, setRecherche] = useState('');
  const selection = valeursFiltre(value);
  const active = filtreActif(value);

  const visibles = options.filter((o) =>
    !recherche || o.label.toLowerCase().includes(recherche.toLowerCase()));

  // Ce qui est cochable en masse : tout sauf les modes exclusifs.
  const cochables = options.filter((o) => !modes.includes(o.value)).map((o) => o.value);
  const visiblesCochables = visibles.filter((o) => !modes.includes(o.value)).map((o) => o.value);
  const toutCoche = visiblesCochables.length > 0
    && visiblesCochables.every((v) => selection.includes(v));

  const libelle = (() => {
    if (!active) return (options.find((o) => o.value === (selection[0] || 'all')) || options[0]).label;
    if (selection.length === 1) {
      return (options.find((o) => o.value === selection[0]) || options[0]).label;
    }
    return t('dialer:contacts.filterMenu.selectedCount', { count: selection.length });
  })();

  const basculer = (val) => {
    // Un mode est exclusif : il remplace toute la selection.
    if (modes.includes(val)) { onChange([val]); return; }
    const sansModes = selection.filter((v) => !modes.includes(v));
    const suivant = sansModes.includes(val)
      ? sansModes.filter((v) => v !== val)
      : [...sansModes, val];
    onChange(suivant.length ? suivant : ['all']);
  };

  const toutBasculer = () => {
    if (toutCoche) {
      const reste = selection.filter((v) => !visiblesCochables.includes(v));
      onChange(reste.length ? reste : ['all']);
    } else {
      const sansModes = selection.filter((v) => !modes.includes(v));
      onChange([...new Set([...sansModes, ...visiblesCochables])]);
    }
  };

  return (
    <Popover onOpenChange={(o) => { if (!o) setRecherche(''); }}>
      <PopoverTrigger asChild>
        <button
          data-testid={`filtre-${label}`}
          className="inline-flex items-center gap-1 rounded-vf px-2 h-7 text-[11px] font-medium transition-colors"
          style={{
            background: active ? 'var(--vf-accent-bg)' : 'var(--vf-surface-2)',
            border: `1px solid ${active ? 'var(--vf-accent-bd)' : 'var(--vf-border)'}`,
            color: active ? 'var(--vf-accent)' : 'var(--vf-text-2)',
            whiteSpace: 'nowrap',
          }}
        >
          <span style={{ textTransform: 'uppercase', letterSpacing: '0.02em', fontSize: 10, fontWeight: 600, opacity: 0.8 }}>
            {label}
          </span>
          <span>{libelle}</span>
          <ArrowDown className="w-3 h-3 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="p-1"
        style={{ background: 'var(--vf-surface-2)', borderColor: 'var(--vf-border)', minWidth: 210 }}
      >
        {/* #5 — search bar, en haut de CHAQUE filtre */}
        <div
          className="flex items-center gap-1.5 px-2 mb-1 rounded"
          style={{ background: 'var(--vf-bg)', border: '1px solid var(--vf-border)', height: 30 }}
        >
          <Search className="w-3 h-3 opacity-50" style={{ flexShrink: 0 }} />
          <input
            data-testid="filtre-recherche"
            value={recherche}
            onChange={(e) => setRecherche(e.target.value)}
            placeholder={t('common:actions.search')}
            className="w-full bg-transparent outline-none text-[12px]"
            style={{ color: 'var(--vf-text-1)' }}
          />
        </div>

        {/* #5 — « Tout cocher » / « Tout décocher », sur ce que la recherche montre */}
        {visiblesCochables.length > 0 && (
          <button
            data-testid="filtre-tout-cocher"
            onClick={toutBasculer}
            className="w-full text-left px-2.5 py-1.5 rounded text-[12px] font-medium transition-colors"
            style={{ color: 'var(--vf-accent)' }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--vf-surface-3)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            {toutCoche ? t('dialer:contacts.filterMenu.uncheckAll') : t('dialer:contacts.filterMenu.checkAll')}
          </button>
        )}

        <div className="space-y-0.5" style={{ maxHeight: 280, overflowY: 'auto' }}>
          {visibles.length === 0 && (
            <p className="px-2.5 py-2 text-[12px]" style={{ color: 'var(--vf-text-3)' }}>{t('dialer:contacts.filterMenu.noResults')}</p>
          )}
          {visibles.map((opt) => {
            const coche = selection.includes(opt.value)
              || (!active && opt.value === 'all');
            const estMode = modes.includes(opt.value);
            return (
              <button
                key={opt.value}
                data-testid={`filtre-option-${opt.value}`}
                onClick={() => basculer(opt.value)}
                className="w-full text-left px-2.5 py-1.5 rounded text-[12px] transition-colors flex items-center gap-2"
                style={{
                  background: coche ? 'var(--vf-accent-bg)' : 'transparent',
                  color: coche ? 'var(--vf-accent)' : 'var(--vf-text-1)',
                  fontWeight: coche ? 600 : 400,
                }}
                onMouseEnter={(e) => { if (!coche) e.currentTarget.style.background = 'var(--vf-surface-3)'; }}
                onMouseLeave={(e) => { if (!coche) e.currentTarget.style.background = 'transparent'; }}
              >
                {/* Case a cocher — un mode reste un choix de vue, pas une case */}
                <span
                  aria-hidden
                  style={{
                    width: 14, height: 14, flexShrink: 0,
                    borderRadius: estMode ? 99 : 3,
                    border: `1.5px solid ${coche ? 'var(--vf-accent)' : 'var(--vf-border)'}`,
                    background: coche ? 'var(--vf-accent)' : 'transparent',
                    display: 'grid', placeItems: 'center',
                  }}
                >
                  {coche && (
                    <svg width="9" height="9" viewBox="0 0 12 12" fill="none">
                      <path d="M2.5 6.3L4.8 8.6L9.5 3.5" stroke="#fff" strokeWidth="2.2"
                        strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </span>
                <span style={{ flex: 1 }}>{opt.label}</span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Renders its children in the list row of XDialTopBar when that row is
// mounted (slot divs #xdial-list-actions-*), inline otherwise.

// Column picker (W4, B2-liste): its own button on the list toolbar. It was
// nested inside the « ⋮ » menu, and rendered nothing at all when the list had
// no imported field, so a base column hidden from its header could never be
// shown again (BUGS-OUVERTS.md, LIST-COLS-1).
export function ColumnsMenu({
  columnLabels, showCol, onToggleCol,
  availableCustomKeys, visibleCustomKeys, onToggleCustomColumn, formatCustomLabel,
}) {
  const { t } = useTranslation(['dialer', 'common']);
  const customKeys = availableCustomKeys || [];
  const baseEntries = Object.entries(columnLabels || {});
  const hiddenCount = baseEntries.filter(([key]) => !showCol(key)).length;
  const checkbox = (on) => (
    <span
      aria-hidden
      className="shrink-0 w-4 h-4 rounded border flex items-center justify-center text-[10px]"
      style={{
        background: on ? 'var(--vf-accent)' : 'var(--vf-bg)',
        borderColor: on ? 'var(--vf-accent)' : 'var(--vf-border)',
        color: '#fff',
      }}
    >
      {on ? '✓' : ''}
    </span>
  );
  return (
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              data-testid="bouton-colonnes"
              className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-vf text-xs font-medium"
              style={{ background: 'var(--vf-surface-2)', border: '1px solid var(--vf-border)', color: 'var(--vf-text-2)' }}
            >
              <Columns3 className="w-3.5 h-3.5" aria-hidden />
              {t('dialer:contacts.columnsPanel.columns')}
              {hiddenCount > 0 && (
                <span className="vf-font-mono text-[10px]" style={{ color: 'var(--vf-text-3)' }}>
                  {t('dialer:contacts.columnsPanel.hiddenCount', { count: hiddenCount })}
                </span>
              )}
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-64 p-2" style={{ background: 'var(--vf-bg)', borderColor: 'var(--vf-border)' }}>
            {/* #42 — les colonnes de base rejoignent le panneau : on peut en
                retirer et en remettre, et le choix est propre a la liste. */}
            <div className="px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: 'var(--vf-text-3)', borderBottom: '1px solid var(--vf-border)' }}>
              {t('dialer:contacts.columnsPanel.tableColumns')}
            </div>
            <div role="group" aria-label={t('dialer:contacts.columnsPanel.tableColumns')} className="max-h-56 overflow-y-auto space-y-0.5 mb-2">
              {baseEntries.map(([key, label]) => {
                const on = showCol(key);
                return (
                  <button
                    key={key}
                    type="button"
                    role="menuitemcheckbox"
                    aria-checked={on}
                    onClick={() => onToggleCol(key)}
                    className="w-full flex items-center justify-between gap-2 px-2 py-1.5 text-xs rounded hover:bg-[var(--vf-surface-2)] transition-colors"
                    style={{ color: 'var(--vf-text-1)' }}
                  >
                    <span className="truncate text-left">{label}</span>
                    {checkbox(on)}
                  </button>
                );
              })}
            </div>
            {customKeys.length > 0 && (
              <>
                <div className="px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: 'var(--vf-text-3)', borderBottom: '1px solid var(--vf-border)' }}>
                  {t('dialer:contacts.columnsPanel.customFields')}
                </div>
                <div role="group" aria-label={t('dialer:contacts.columnsPanel.customFields')} className="max-h-64 overflow-y-auto space-y-0.5">
                  {customKeys.map((k) => {
                    const on = visibleCustomKeys.includes(k);
                    return (
                      <button
                        key={k}
                        type="button"
                        role="menuitemcheckbox"
                        aria-checked={on}
                        onClick={() => onToggleCustomColumn(k)}
                        className="w-full flex items-center justify-between gap-2 px-2 py-1.5 text-xs rounded hover:bg-[var(--vf-surface-2)] transition-colors"
                        style={{ color: 'var(--vf-text-1)' }}
                      >
                        <span className="truncate text-left">{formatCustomLabel(k)}</span>
                        {checkbox(on)}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </PopoverContent>
        </Popover>
  );
}

export function ListActionsSlot({ slot, children }: { slot: 'view' | 'tools' | 'filters'; children: React.ReactNode }) {
  const [target, setTarget] = useState<HTMLElement | null>(null);
  useLayoutEffect(() => {
    setTarget(document.getElementById(`xdial-list-actions-${slot}`));
  }, [slot]);
  return target ? createPortal(children, target) : <>{children}</>;
}

export function VFFilterBar({
  searchInput, onSearchChange,
  prospectFilters, setProspectFilters,
  filteredCount, totalCount,
  viewMode, setViewMode, setKanbanEnabled,
  availableCustomKeys, visibleCustomKeys, onToggleCustomColumn, formatCustomLabel,
  columnLabels, showCol, onToggleCol,
  personaOptions, cityOptions, sortBy, sortDirection, onSort,
}) {
  const { t } = useTranslation(['dialer', 'common']);
  // #5 — un filtre peut porter plusieurs valeurs ; filtreActif() les normalise.
  const hasFilter = filtreActif(prospectFilters.lastCall)
    || filtreActif(prospectFilters.leadStatus)
    || filtreActif(prospectFilters.persona)
    || filtreActif(prospectFilters.city);

  const onClear = () => {
    setProspectFilters({ leadStatus: 'all', priority: 'all', lastCall: 'all', score: 'all', persona: 'all', city: 'all' });
    onSearchChange('');
  };

  // Persona and City were removed on 2026-09-23 (client request): a value
  // left in either would keep filtering the list with no visible control.
  useEffect(() => {
    // The score was removed the same day: same reset.
    if (filtreActif(prospectFilters.persona) || filtreActif(prospectFilters.city) || filtreActif(prospectFilters.score)) {
      setProspectFilters({ ...prospectFilters, persona: 'all', city: 'all', score: 'all' });
    }
  }, [prospectFilters, setProspectFilters]);

  // The filter menus live behind one button on the status bar row; the
  // button shows how many are active while they are folded away. The search
  // is always visible (W4, B2-liste): it used to be folded away too.
  const [filtersOpen, setFiltersOpen] = useState(false);
  // Raccourci `/` (useDialerDomEvents) : place le curseur dans la recherche.
  useEffect(() => {
    const focus = () => document.getElementById('queue-search')?.focus();
    window.addEventListener('xdial-focus-list-search', focus);
    return () => window.removeEventListener('xdial-focus-list-search', focus);
  }, []);
  const activeCount = [prospectFilters.lastCall, prospectFilters.leadStatus]
    .filter((v) => filtreActif(v)).length + (sortBy ? 1 : 0);

  return (
    <div className="flex items-center gap-2 flex-wrap" data-list-search>
      <ListActionsSlot slot="filters">
      {/* Search — always visible (W4, B2-liste). */}
      <div className="relative w-52" role="search">
        <Search
          className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5"
          style={{ color: 'var(--vf-text-3)' }}
          aria-hidden
        />
        <input
          id="queue-search"
          type="search"
          placeholder={t('dialer:filters.search')}
          aria-label={t('dialer:filters.search')}
          value={searchInput}
          onChange={(e) => onSearchChange(e.target.value)}
          className="w-full h-8 rounded-vf pl-8 pr-2 text-[12px] outline-none focus-visible:ring-2 focus-visible:ring-[var(--vf-accent)]"
          style={{
            background: 'var(--vf-surface-2)',
            border: '1px solid var(--vf-border)',
            color: 'var(--vf-text-1)',
          }}
        />
      </div>
      <button
        data-testid="bouton-filtres"
        onClick={() => setFiltersOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-vf text-xs font-medium transition-colors"
        style={{
          background: filtersOpen ? 'var(--vf-accent-bg)' : 'var(--vf-surface-2)',
          border: '1px solid var(--vf-border)',
          color: filtersOpen ? 'var(--vf-accent)' : 'var(--vf-text-2)',
        }}
        aria-expanded={filtersOpen}
      >
        <SlidersHorizontal className="w-3.5 h-3.5" aria-hidden />
        {t('dialer:filters.toggle')}
        {!filtersOpen && activeCount > 0 && (
          <span
            className="vf-font-mono text-[11px] font-semibold rounded-full px-1"
            style={{ background: 'var(--vf-primary)', color: 'var(--vf-primary-fg)', lineHeight: '15px', minWidth: 15, textAlign: 'center' }}
          >
            {activeCount}
          </span>
        )}
      </button>
      {filtersOpen && (
      <div className="flex items-center gap-1.5">
      {/* STATUT APPEL */}
      <VFFilterDropdown
        label={t('dialer:filters.callStatus').toUpperCase()}
        value={prospectFilters.lastCall || 'all'}
        options={optionsTraduites(FILTER_STATUT_APPEL, t)}
        /* #5 — « Prêts à appeler » et « Tout afficher » sont des VUES, pas des
           valeurs cumulables : les cocher remplace la sélection. */
        modes={['all', 'show_all']}
        onChange={(v) => setProspectFilters({ ...prospectFilters, lastCall: v })}
      />

      {/* STATUT LEAD */}
      <VFFilterDropdown
        label={t('dialer:filters.leadStatus').toUpperCase()}
        value={prospectFilters.leadStatus || 'all'}
        options={optionsTraduites(FILTER_STATUT_LEAD, t)}
        onChange={(v) => setProspectFilters({ ...prospectFilters, leadStatus: v })}
      />

      {/* SORT BY — #43 : le tri n'existait que via un clic sur l'en-tete de
          colonne, geste invisible pour qui ne le connait pas. Controle explicite.

          2026-09-15 — ce controle affichait `sortBy || 'name'`, donc « Nom »,
          alors que sortBy vaut null au chargement et que le comparateur sort
          aussitot sur `if (!sortBy) return 0`. Il annoncait donc un tri que le
          code refusait d'appliquer. La liste n'etait pas en desordre pour
          autant : elle suit l'ordre d'appel (dialIntent, ADR-031), qui est
          l'ordre VOULU pour que l'agent voie comment le Power Mode va
          composer. On nomme desormais cet ordre au lieu de le travestir, et on
          permet d'y revenir.

          Second defaut du meme controle : un tri est mono-valeur, mais
          VFFilterDropdown coche en multi-selection par defaut, si bien que
          choisir un 2e critere affichait « 2 selectionnes » et empilait les
          criteres. La prop `modes` declare des options EXCLUSIVES — basculer()
          fait alors onChange([val]) et remplace la selection. Le composant
          savait deja le faire ; ce controle ne le lui demandait pas.
          Cf. BUGS-OUVERTS.md (SORT-1). */}
      <VFFilterDropdown
        label={t('dialer:contacts.filters.sort.label').toUpperCase()}
        value={sortBy || 'default'}
        options={optionsTraduites(FILTER_TRI, t)}
        modes={['default', 'name', 'position', 'company', 'lastContact', 'status']}
        onChange={(v) => onSort && onSort(Array.isArray(v) ? v[0] : v)}
      />


      {/* Clear — #13 : ce bouton etait rendu CONDITIONNELLEMENT. Son apparition
          des qu'un filtre ou une recherche devenait actif poussait tout le bloc
          de droite de 15 px — c'est le deplacement du bouket de vue signale par
          le client. Mesure a l'ecran : barre de 11 a 12 enfants, bouton de vue
          x = 1011 -> 1026.
          Il occupe desormais toujours sa place ; quand il n'y a rien a effacer
          il est invisible ET inerte (hors clavier, hors lecteur d'ecran). */}
      {(() => {
        const actif = !!(hasFilter || searchInput); // clear-toujours-present
        return (
          <button
            data-testid="bouton-effacer"
            onClick={actif ? onClear : undefined}
            tabIndex={actif ? 0 : -1}
            aria-hidden={actif ? undefined : true}
            className="inline-flex items-center gap-1 rounded-vf px-2 h-7 text-[11px] font-medium transition-colors"
            style={{
              background: 'transparent',
              color: 'var(--vf-text-2)',
              border: '1px solid var(--vf-border)',
              visibility: actif ? 'visible' : 'hidden',
              pointerEvents: actif ? 'auto' : 'none',
              flexShrink: 0,
            }}
            onMouseEnter={(e) => { if (actif) { e.currentTarget.style.color = 'var(--vf-red)'; e.currentTarget.style.borderColor = 'var(--vf-red)'; } }}
            onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--vf-text-2)'; e.currentTarget.style.borderColor = 'var(--vf-border)'; }}
            title={t('dialer:filters.clear')}
          >
            <X className="w-3.5 h-3.5" />
            {t('dialer:filters.clear')}
          </button>
        );
      })()}

      </div>
      )}
      </ListActionsSlot>

      {/* The "193 / 200" result count was removed on 2026-09-23 (client
          request: it told the agent nothing). */}
    </div>
  );
}
